import { useState } from 'react'

import { get } from 'lodash'
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Grid,
  Paper,
  Typography,
} from '@mui/material'
import {
  BorderAllRounded,
  PictureAsPdfRounded,
} from '@mui/icons-material'
import { useIntl } from 'react-intl'

import { DemographicsFilters, PulseSurveyStatistics } from 'components'
import { DemographicsFiltersType } from 'components/DemographicsFilters'
import { downloadPulseSurveyReportRequest } from 'core/requests'
import { t } from 'i18n'

export default function PulseSurveyInsightsComponent(
  props: {
    pulseSurveyId: number | any
    pulseSurveyOverview: any
    isPulseSurveyOverviewLoading: boolean
    getPulseSurveyOverview: any
    filtersQuantityState: any
  } & Omit<DemographicsFiltersType, 'refetch'>,
) {
  const {
    pulseSurveyId,
    pulseSurveyOverview,
    isPulseSurveyOverviewLoading,
    getPulseSurveyOverview,
    filtersQuantityState,
    filtersState,
  } = props

  const [showFilters, setShowFilters] = useState(false)
  const [isReportLoading, setIsReportLoading] = useState(false)

  const intl = useIntl()

  const pulseSurveyData = get(pulseSurveyOverview, 'survey')

  const hasOpenEndedAnswers = pulseSurveyData?.questions?.some(
    (q: any) => q.open_ended_answers?.length > 0,
  )

  const downloadButtons = [
    {
      label: intl.formatMessage({ id: 'beheardReport.fullReport.buttonLabels.xlsx' }),
      Icon: BorderAllRounded,
      type: 'xlsx' as const,
    },
    {
      label: intl.formatMessage({ id: 'beheardReport.fullReport.buttonLabels.pdf' }),
      Icon: PictureAsPdfRounded,
      type: 'pdf' as const,
    },
  ]

  return (
    <Grid container>
      <Grid item xs={12}>
        {isPulseSurveyOverviewLoading ? (
          <Box width={1} my={5}>
            <Grid container justifyContent='center'>
              <CircularProgress size={40} />
            </Grid>
          </Box>
        ) : (
          <>
            <Box width={1} mb={2}>
              <Grid
                container
                spacing={2}
                alignItems='center'
                justifyContent='space-between'
              >
                <Grid item>
                  <Typography variant='h6' color='text.secondary'>
                    {get(pulseSurveyOverview, 'survey.name')} Insights
                  </Typography>
                </Grid>

                {location.pathname.startsWith('/manage-pulse-surveys/') &&
                  location.pathname.endsWith('insights') && (
                    <Grid item xs='auto'>
                      <Grid container justifyContent='flex-end' alignItems='center'>
                        <Badge
                          badgeContent={filtersQuantityState}
                          color='secondary'
                        >
                          <Button
                            variant='outlined'
                            color='secondary'
                            onClick={() => setShowFilters(!showFilters)}
                            size='large'
                          >
                            {t('beheardReport.filterButtons.filters')}
                          </Button>
                        </Badge>
                      </Grid>
                    </Grid>
                  )}
              </Grid>
            </Box>

            <Grid container>
              <Grid item xs={12}>
                {location.pathname.startsWith('/manage-pulse-surveys/') &&
                  location.pathname.endsWith('insights') && (
                    <Collapse in={showFilters}>
                      <Grid container>
                        <Grid item xs={12}>
                          <Box mb={3}>
                            <Paper square>
                              <Box p={2}>
                                <DemographicsFilters
                                  {...props}
                                  hasResetFilters
                                  refetch={getPulseSurveyOverview}
                                />
                              </Box>
                            </Paper>
                          </Box>
                        </Grid>
                      </Grid>
                    </Collapse>
                  )}
              </Grid>
            </Grid>

            {pulseSurveyOverview && !pulseSurveyOverview.errors ? (
              <>
                <PulseSurveyStatistics
                  pulseSurvey={pulseSurveyData}
                />

                {hasOpenEndedAnswers && (
                  <Box my={2} width={1}>
                    <Grid container>
                      <Grid item xs={12}>
                        <Typography
                          variant='h5'
                          color='text.primary'
                          fontWeight={600}
                          paragraph
                        >
                          {t('beheardReport.fullReport.openEndedQuestions')}
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Grid container spacing={1}>
                          {downloadButtons.map(btn => (
                            <Grid item key={btn.type}>
                              <Button
                                style={{ minWidth: 300 }}
                                variant='outlined'
                                color='primary'
                                disabled={isReportLoading}
                                startIcon={<btn.Icon />}
                                onClick={() =>
                                  downloadPulseSurveyReportRequest({
                                    surveyId: Number(pulseSurveyId),
                                    type: btn.type,
                                    filtersState,
                                    setIsReportLoading,
                                    openInNewTab: false,
                                  })
                                }
                              >
                                {btn.label}
                              </Button>
                            </Grid>
                          ))}
                        </Grid>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} textAlign={"center"}>
                  <Typography variant='h5' color='text.secondary'>
                    {t('pulseSurveyOverview.whenSurveyCloses')}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </>
        )}
      </Grid>
    </Grid>
  )
}
